import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { OutlineFieldComponent } from '../outline-field/outline-field.component';
import {
  DocumentTemplate,
  RECETA_TEMPLATE_ID,
  TEMPLATE_LIST_DETAIL_SEPARATOR,
  parseTemplateListText,
  resolvePlaceholderText,
} from '../../models/document-template.type';
import { Patient } from '../../models/patient.type';
import { Prescription } from '../../models/appointment.type';
import { DocumentTemplateService } from '../../services/database/document-template.service';
import { MembershipService } from '../../services/auth/membership.service';
import { loadLogoForPdf } from '../../utils/prescription-pdf.util';
import { buildTemplateDocumentPdf } from '../../utils/template-pdf.util';
import {
  TemplateFillField,
  buildInitialFieldValues,
  listTemplateFillFields,
  templateUsesDoctorPlaceholder,
} from '../../utils/template-fill.util';

export interface GenerateDocumentDoctorOption {
  uid: string;
  label: string;
}

export interface GenerateDocumentDialogData {
  patient: Patient;
  /** Used when no doctor picker (e.g. doctor generating from Consultas). */
  doctorLabel?: string;
  dateLabel: string;
  /**
   * When provided, a doctor dropdown is shown if the selected template
   * references doctor placeholders (secretaries / patient profile flow).
   */
  doctors?: GenerateDocumentDoctorOption[];
  /** Consulta prescriptions; prefill the medicamentos list. */
  medications?: Prescription[];
  /** Template selected when the dialog opens (e.g. the receta). */
  templateId?: string;
}

@Component({
  selector: 'app-generate-document-dialog',
  templateUrl: './generate-document-dialog.component.html',
  styleUrls: ['./generate-document-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
    OutlineFieldComponent,
  ],
})
export class GenerateDocumentDialogComponent {
  readonly dialogRef = inject(MatDialogRef<GenerateDocumentDialogComponent>);
  readonly data = inject<GenerateDocumentDialogData>(MAT_DIALOG_DATA);
  private templatesApi = inject(DocumentTemplateService);
  private membership = inject(MembershipService);

  readonly doctors = this.data.doctors ?? [];

  readonly loading = signal(true);
  readonly exporting = signal(false);
  readonly printing = signal(false);
  readonly error = signal<string | null>(null);
  readonly templates = signal<DocumentTemplate[]>([]);
  readonly selectedTemplateId = signal<string | null>(null);
  readonly selectedDoctorUid = signal<string | null>(null);
  readonly fieldValues = signal<Record<string, string>>({});

  readonly selectedTemplate = computed(() => {
    const id = this.selectedTemplateId();
    if (!id) {
      return null;
    }
    return this.templates().find((item) => item.id === id) ?? null;
  });

  readonly needsDoctorPicker = computed(() => {
    const template = this.selectedTemplate();
    return (
      this.doctors.length > 0 &&
      !!template &&
      templateUsesDoctorPlaceholder(template)
    );
  });

  readonly selectedDoctorLabel = computed(() => {
    const uid = this.selectedDoctorUid();
    if (uid) {
      return this.doctors.find((doctor) => doctor.uid === uid)?.label?.trim() || '';
    }
    return this.data.doctorLabel?.trim() || '';
  });

  readonly fillFields = computed(() => {
    const template = this.selectedTemplate();
    return template ? listTemplateFillFields(template) : [];
  });

  readonly manualFields = computed(() =>
    this.fillFields().filter((field) => field.fillMode === 'manual')
  );

  readonly autoFields = computed(() =>
    this.fillFields().filter((field) => field.fillMode === 'auto')
  );

  readonly missingManualLabels = computed(() => {
    const values = this.fieldValues();
    return this.manualFields()
      .filter((field) => !values[field.key]?.trim())
      .map((field) => field.label);
  });

  readonly missingDoctor = computed(
    () => this.needsDoctorPicker() && !this.selectedDoctorLabel()
  );

  readonly canExport = computed(
    () =>
      !!this.selectedTemplate() &&
      this.missingManualLabels().length === 0 &&
      !this.missingDoctor()
  );

  readonly previewLines = computed(() => {
    const template = this.selectedTemplate();
    if (!template) {
      return [];
    }
    const values = this.fieldValues();
    const lines: string[] = [];
    for (const block of template.blocks) {
      if (block.type === 'text') {
        const text = resolvePlaceholderText(block.content, values).trim();
        if (text) {
          lines.push(text);
        }
      } else if (block.type === 'placeholder') {
        lines.push(values[block.key]?.trim() || `{{${block.key}}}`);
      } else if (block.type === 'list') {
        parseTemplateListText(values[block.key]).forEach((item, index) => {
          const prefix = block.listStyle === 'bullets' ? '•' : `${index + 1}.`;
          const detail = item.detail ? `${TEMPLATE_LIST_DETAIL_SEPARATOR}${item.detail}` : '';
          lines.push(`${prefix} ${item.title}${detail}`);
        });
      } else if (block.type === 'signature') {
        lines.push(`— ${block.label || 'Firma'} —`);
      } else if (block.type === 'divider') {
        lines.push('————————');
      }
    }
    return lines;
  });

  constructor() {
    if (this.doctors.length > 0) {
      const preferred = this.data.patient.doctorId;
      const match = preferred
        ? this.doctors.find((doctor) => doctor.uid === preferred)
        : undefined;
      this.selectedDoctorUid.set(match?.uid ?? this.doctors[0]?.uid ?? null);
    }
    void this.loadTemplates();
  }

  onTemplateChange(templateId: string): void {
    this.selectedTemplateId.set(templateId);
    this.resetFieldValues();
  }

  onDoctorChange(uid: string): void {
    this.selectedDoctorUid.set(uid);
    const label =
      this.doctors.find((doctor) => doctor.uid === uid)?.label?.trim() || '';
    this.updateFieldValue('doctor.label', label);
  }

  fieldValue(key: string): string {
    return this.fieldValues()[key] || '';
  }

  updateFieldValue(key: string, value: string): void {
    this.fieldValues.update((current) => ({ ...current, [key]: value }));
  }

  trackField(_index: number, field: TemplateFillField): string {
    return field.key;
  }

  async loadTemplates(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const all = await this.templatesApi.listTemplates();
      const recetaOnly = this.data.templateId === RECETA_TEMPLATE_ID;
      const list = all.filter((item) =>
        recetaOnly ? item.id === RECETA_TEMPLATE_ID : item.id !== RECETA_TEMPLATE_ID
      );
      this.templates.set(list);
      const preferred = list.find((item) => item.id === this.data.templateId);
      this.selectedTemplateId.set(preferred?.id ?? list[0]?.id ?? null);
      this.resetFieldValues();
      if (!list.length) {
        this.error.set(
          'No hay plantillas. Créelas en el menú Plantillas.'
        );
      }
    } catch (err) {
      console.error(err);
      this.error.set('No se pudieron cargar las plantillas.');
    } finally {
      this.loading.set(false);
    }
  }

  async exportPdf(): Promise<void> {
    const template = this.selectedTemplate();
    if (!this.ensureReadyToGenerate(template)) {
      return;
    }

    this.exporting.set(true);
    this.error.set(null);
    try {
      const pdf = await this.buildPdf(template!, this.fieldValues());
      const patientName = this.data.patient.name?.trim() || 'Documento';
      pdf.save(`${patientName} - ${template!.name}.pdf`);
      this.dialogRef.close({ exported: true });
    } catch (err) {
      console.error(err);
      this.error.set('No se pudo generar el PDF.');
    } finally {
      this.exporting.set(false);
    }
  }

  async printPdf(): Promise<void> {
    const template = this.selectedTemplate();
    if (!this.ensureReadyToGenerate(template)) {
      return;
    }

    this.printing.set(true);
    this.error.set(null);
    try {
      const pdf = await this.buildPdf(template!, this.fieldValues());
      this.openBrowserPrintPreview(pdf);
    } catch (err) {
      console.error(err);
      this.error.set('No se pudo abrir la vista de impresión.');
    } finally {
      this.printing.set(false);
    }
  }

  private ensureReadyToGenerate(
    template: DocumentTemplate | null
  ): template is DocumentTemplate {
    if (!template || !this.canExport()) {
      if (this.missingDoctor()) {
        this.error.set('Seleccione el doctor para este documento.');
      } else if (this.missingManualLabels().length) {
        this.error.set(
          `Complete: ${this.missingManualLabels().join(', ')}`
        );
      }
      return false;
    }
    return true;
  }

  private async buildPdf(
    template: DocumentTemplate,
    values: Record<string, string>
  ) {
    const logoPath = this.membership.selectedBusinessLogoStoragePath();
    let logo: Awaited<ReturnType<typeof loadLogoForPdf>>;
    if (template.includeBusinessHeader !== false && logoPath) {
      const logoUrl = await this.membership.businessLogoUrl(logoPath);
      logo = logoUrl ? await loadLogoForPdf(logoUrl) : undefined;
    }

    return buildTemplateDocumentPdf({
      template,
      values,
      businessName: this.membership.selectedBusinessName(),
      logo,
    });
  }

  /** Opens the PDF in a hidden frame and triggers the browser print dialog. */
  private openBrowserPrintPreview(pdf: ReturnType<typeof buildTemplateDocumentPdf>): void {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const frame = document.createElement('iframe');
    frame.setAttribute('title', 'Vista previa de impresión');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.src = url;
    document.body.appendChild(frame);

    const cleanup = (): void => {
      URL.revokeObjectURL(url);
      frame.remove();
    };

    frame.onload = () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch (err) {
        console.error(err);
        // Fallback: open PDF in a new tab so the user can print from there.
        const tab = window.open(url, '_blank');
        if (!tab) {
          this.error.set(
            'Permita ventanas emergentes para imprimir el documento.'
          );
          cleanup();
          return;
        }
      }
      // Keep the blob alive while the print dialog is open.
      window.setTimeout(cleanup, 60_000);
    };
  }

  private resetFieldValues(): void {
    const template = this.selectedTemplate();
    if (!template) {
      this.fieldValues.set({});
      return;
    }
    this.fieldValues.set(
      buildInitialFieldValues(template, {
        patient: this.data.patient,
        doctorLabel: this.selectedDoctorLabel(),
        businessName: this.membership.selectedBusinessName(),
        dateLabel: this.data.dateLabel,
        medications: this.data.medications,
      })
    );
  }
}
